import type React from 'react';
import type { BudgetActualReport } from '../../types/reporting.types';
import { formatEuro, formatEuroCompact } from '../../utils/currency';

interface FinancialOverviewCardProps {
  combinedSummary: BudgetActualReport['combined_summary'];
}

const FinancialOverviewCard: React.FC<FinancialOverviewCardProps> = ({ combinedSummary }) => {
  const incomeVariance =
    combinedSummary.total_actual_income - combinedSummary.total_budgeted_income;
  const expensesVariance =
    combinedSummary.total_budgeted_expenses - combinedSummary.total_actual_expenses;

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Overview</h3>

        {/* Income Section */}
        <div className="space-y-2 mb-4">
          <h4 className="text-sm font-medium text-gray-900 border-b pb-1">Income</h4>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Budgeted</span>
            <span className="font-medium text-blue-600">
              {formatEuro(combinedSummary.total_budgeted_income)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Actual</span>
            <span className="font-medium text-green-600">
              {formatEuro(combinedSummary.total_actual_income)}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Variance</span>
            <span
              className={`font-medium ${incomeVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              {incomeVariance >= 0 ? '+' : ''}
              {formatEuroCompact(Math.abs(incomeVariance))}
            </span>
          </div>
        </div>

        {/* Expenses Section */}
        <div className="space-y-2 mb-4">
          <h4 className="text-sm font-medium text-gray-900 border-b pb-1">Expenses</h4>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Budgeted</span>
            <span className="font-medium text-blue-600">
              {formatEuro(combinedSummary.total_budgeted_expenses)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Actual</span>
            <span className="font-medium text-orange-600">
              {formatEuro(combinedSummary.total_actual_expenses)}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Variance</span>
            <span
              className={`font-medium ${expensesVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              {expensesVariance >= 0 ? '+' : ''}
              {formatEuroCompact(Math.abs(expensesVariance))}
            </span>
          </div>
        </div>

        {/* Net Result */}
        <div className="border-t pt-3">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium text-gray-900">
              Operational result (ex. VAT &amp; taxes)
            </span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-500">Budgeted Net</span>
            <span className="font-medium text-blue-600">
              {formatEuro(combinedSummary.total_budgeted_net)}
            </span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-500">Actual Net</span>
            <span className="font-medium text-purple-600">
              {formatEuro(combinedSummary.total_actual_net)}
            </span>
          </div>
        </div>

        {/* Tax reconciliation — only shown when tax transactions exist */}
        {combinedSummary.tax_net !== 0 && (
          <div className="border-t pt-3 mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Tax payments (verot)</span>
              <span
                className={`font-medium ${combinedSummary.tax_net >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                {combinedSummary.tax_net >= 0 ? '+' : ''}
                {formatEuro(combinedSummary.tax_net)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-gray-900">Actual cash flow</span>
              <span
                className={`font-semibold ${combinedSummary.actual_cash_flow >= 0 ? 'text-green-700' : 'text-red-700'}`}
              >
                {combinedSummary.actual_cash_flow >= 0 ? '+' : ''}
                {formatEuro(combinedSummary.actual_cash_flow)}
              </span>
            </div>
          </div>
        )}

        {/* Net Variance badge */}
        <div className="border-t pt-3">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-900">Net Variance</span>
            <span
              className={`text-sm font-medium ${
                combinedSummary.total_variance >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {combinedSummary.total_variance >= 0 ? '+' : ''}
              {formatEuro(Math.abs(combinedSummary.total_variance))}
            </span>
          </div>
          <div className="text-center mt-2">
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                combinedSummary.overall_performance === 'on_target'
                  ? 'bg-blue-100 text-blue-800'
                  : combinedSummary.overall_performance === 'over_budget'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-green-100 text-green-800'
              }`}
            >
              {combinedSummary.overall_performance.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialOverviewCard;
